import { ReactNode } from "react";
import { useRecordContext } from "react-admin";
import { Box, Divider, Typography } from "@mui/material";
import { useAppTheme } from "@/hooks/useAppTheme";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import type { AccommodationRun } from "@/types";
import { LabeledValue } from "./LabeledValue";

// accommodation_factor is the only nullable column on agg_accommodation_runs (omitempty in the
// Go domain type). All other numeric fields are NOT NULL DEFAULT 0 in the DB.
//
// The null/NaN guard is deliberate: a literal 0 factor is falsy but valid (an early-policy
// month where no accommodation has accrued yet), so `if (!v)` would have rendered "0.00%" as
// "—". Only treat missing (null/undefined) or non-finite values as unrenderable.
const formatFactor = (v: number | undefined): string => {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(2)}%`;
};

type DetailSectionProps = { label: string; first?: boolean; children: ReactNode };

const DetailSection = ({ label, first, children }: DetailSectionProps) => (
  <>
    {!first && <Divider sx={{ my: 2 }} />}
    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
      {label}
    </Typography>
    <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
      {children}
    </Box>
  </>
);

export const AccommodationRunExpandPanel = () => {
  const record = useRecordContext<AccommodationRun>();
  const { isDarkMode } = useAppTheme();
  if (!record) return null;

  // Backend guarantees both amounts are always >= 0 — reconciliation_calculator.go applies
  // decimal.Max(value, zero) before persisting. A clean year-end where both amounts land at
  // exactly 0.00 has nothing to display, so hiding the section is correct in that case.
  // If the backend later exposes an explicit is_year_end flag, replace this gate.
  const isYearEnd = record.clawback_amount > 0 || record.additional_reimbursement_amount > 0;

  return (
    <Box sx={{ p: 2, backgroundColor: isDarkMode ? "background.paper" : "background.default" }}>
      <DetailSection label="Inputs" first>
        <LabeledValue label="Accommodation Factor" value={formatFactor(record.accommodation_factor)} />
        <LabeledValue label="Annual Attachment" value={formatUSD(record.annual_attachment)} />
        <LabeledValue label="Prorated Attachment" value={formatUSD(record.prorated_attachment)} />
      </DetailSection>

      <DetailSection label="Spend">
        <LabeledValue label="Actual YTD Spend" value={formatUSD(record.actual_ytd_spend)} />
        <LabeledValue label="YTD Specific Funding" value={formatUSD(record.ytd_specific_funding)} />
        <LabeledValue label="Spend Applicable to Agg" value={formatUSD(record.spend_applicable_to_agg)} />
      </DetailSection>

      <DetailSection label="Result">
        <LabeledValue label="Monthly Position" value={formatUSD(record.spend_over_deductible)} />
        <LabeledValue label="Funding Amount" value={formatUSD(record.funding_amount)} />
      </DetailSection>

      {isYearEnd && (
        <DetailSection label="Year-End Reconciliation">
          <LabeledValue label="Clawback Amount" value={formatUSD(record.clawback_amount)} />
          <LabeledValue
            label="Additional Reimbursement Amount"
            value={formatUSD(record.additional_reimbursement_amount)}
          />
        </DetailSection>
      )}
    </Box>
  );
};
